import { Injectable, inject, signal, computed } from '@angular/core';
import {MenuItem} from 'primeng/api';
import { MenuAdminService } from './menu/menu-admin.service';
import { MenuUserService } from './menu/menu-user.service';

export type UserRole = 'Administrador' | 'Propietario/Inquilino' | 'Proveedores' | 'Portero/Vigilante/Mantenimiento';

const MODULE_CODES: Record<string, string> = {
  'MENU_ADMIN.FINANCE': 'finance',
  'MENU_ADMIN.OPERATIONS': 'operations',
  'MENU_ADMIN.COMMUNITY': 'community',
  'MENU_ADMIN.MARKETPLACE': 'marketplace',
  'MENU_ADMIN.INTELLIGENCE': 'intelligence',
  'MENU_ADMIN.CONFIGURATION': 'configuration',
  'MENU_ADMIN.DASHBOARD': 'dashboard'
};

@Injectable({ providedIn: 'root' })
export class MenuBuilderService {
  private menuAdminService = inject(MenuAdminService);
  private menuUserService = inject(MenuUserService);

  // Signal para el rol actual del usuario
  private currentRole = signal<UserRole>('Administrador');
  private allowedProviderModules = signal<string[]>([]);

  // Getter para el rol actual
  getCurrentRole(): UserRole {
    return this.currentRole();
  }

  // Setter para cambiar el rol
  setCurrentRole(role: UserRole): void {
    this.currentRole.set(role);
  }

  setAllowedProviderModules(modules: string[]): void {
    this.allowedProviderModules.set(modules);
  }

  // Computed signal para el menú que se actualiza reactivamente cuando cambia el rol
  menuItems = computed<MenuItem[]>(() => {
    const role = this.currentRole();

    // Decidir qué menú mostrar basado en el rol
    switch (role) {
      case 'Administrador':
        return this.menuAdminService.buildMenu();

      case 'Proveedores':
        return this.filterProviderMenu(this.menuAdminService.buildMenu());

      case 'Propietario/Inquilino':
      case 'Portero/Vigilante/Mantenimiento':
        return this.menuUserService.buildMenu();

      default:
        return this.menuAdminService.buildMenu();
    }
  });

  buildMenu(): MenuItem[] {
    return this.menuItems();
  }

  private filterProviderMenu(items: MenuItem[]): MenuItem[] {
    const allowed = this.allowedProviderModules();
    if (!allowed.length) return [];

    return items
      .map(item => {
        const code = item.label ? MODULE_CODES[item.label] : undefined;
        if (code && allowed.includes(code)) {
          return item;
        }
        if (item.items) {
          const filteredChildren = item.items.filter(child => {
            const childCode = child.label ? MODULE_CODES[child.label] : undefined;
            return childCode && allowed.includes(childCode);
          });
          if (filteredChildren.length > 0) {
            return { ...item, items: filteredChildren };
          }
        }
        return null;
      })
      .filter((item): item is MenuItem => item !== null);
  }
}
